// Live plumbing for attributed proxy commitments (COM-007–COM-009).
//
// Eligibility and capacity are not decided here. The directory supplies the people,
// the server-supplied affordance decides whether this route exists, and the shared
// claim guard is the only authority on whether the write succeeds.

import type { commitments as contracts } from "@herefor/contracts";
import { useMutation, useQuery } from "convex/react";
import { useCallback, useMemo } from "react";

import { commitmentMutations } from "../../convex/commitments";
import { peopleQueries } from "../../convex/people";
import { useConnectivity } from "../../offline/offline";
import { ProxyCommitmentForm, type ProxyCandidate } from "./ProxyCommitmentSurfaces";
import {
  useProxyCommitment,
  type ProxyRecordChoice,
  type ProxyRecordResultLike,
} from "./useProxyCommitment";

export interface ProxyCommitmentRouteProps {
  readonly circleId: string;
  readonly supportNeedId: string;
  readonly needRevision: number;
  /** Server-decided from `commitments.record.proxy`; never inferred from a role. */
  readonly canRecord: boolean;
  readonly stillNeedsSomeone: boolean;
  readonly viewerPersonId: string;
}

export function ProxyCommitmentRoute({
  circleId,
  supportNeedId,
  needRevision,
  canRecord,
  stillNeedsSomeone,
  viewerPersonId,
}: ProxyCommitmentRouteProps) {
  const directory = useQuery(peopleQueries.list, canRecord ? { circleId } : "skip");
  const recordProxy = useMutation(commitmentMutations.recordProxy);
  const { actionsPaused } = useConnectivity();

  const candidates = useMemo<readonly ProxyCandidate[]>(
    () =>
      directory?.people
        .filter(
          (person) =>
            person.personId !== viewerPersonId &&
            person.proxyOnly &&
            (person.participationState === "active" || person.participationState === "offline") &&
            person.accessState === "active",
        )
        .map((person) => ({ personId: person.personId, name: person.chosenName })) ?? [],
    [directory, viewerPersonId],
  );

  const controller = useProxyCommitment({
    record: useCallback(
      async (choice: ProxyRecordChoice): Promise<ProxyRecordResultLike> => {
        if (actionsPaused) {
          throw new Error("Connection is not ready for a commitment change.");
        }
        const outcome = await recordProxy({
          circleId,
          supportNeedId,
          responsiblePersonId: choice.responsiblePersonId,
          needRevision,
          agreementSource: choice.agreementSource,
          consentConfirmed: true,
          idempotencyKey: crypto.randomUUID(),
        });
        return unwrapProxyRecord(outcome);
      },
      [actionsPaused, recordProxy, circleId, supportNeedId, needRevision],
    ),
  });

  // A denied Membership gets no action and no people query. The mutation remains
  // independently protected by the boundary for stale or forged clients.
  if (!canRecord || directory === undefined) return null;

  return (
    <ProxyCommitmentForm
      canRecord={canRecord}
      stillNeedsSomeone={stillNeedsSomeone}
      candidates={candidates}
      actionsDisabled={actionsPaused}
      state={controller.state}
      onRecord={(choice) => {
        void controller.submit(choice);
      }}
    />
  );
}

function unwrapProxyRecord(
  outcome:
    | { readonly status: "applied"; readonly value: contracts.RecordProxyCommitmentResult }
    | { readonly status: "replayed" },
): contracts.RecordProxyCommitmentResult {
  return outcome.status === "applied"
    ? outcome.value
    : { ok: false, rejection: { kind: "already_committed" } };
}
